import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { classNames } from 'primereact/utils';
import { 
  ValidateAccessToken, 
  PolicyholderAlreadyExists, 
  PolicyholderAlreadyRegistered, 
  PolicyholderAuthCode 
} from '../services/services';
import PolicyNumberSample from '../assets/PolicyNumberSample.jpg';
import PolicyInfoPage from '../assets/PolicyInfoPage.jpg';

const ValidateCode = () => {
  const navigate = useNavigate();
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState({ active: false, message: '', error: false, status: 200 });
  const [isAllowRegister, setIsAllowRegister] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isValidPolicy, setIsValidPolicy] = useState(true);
  const [registeredName, setRegisteredName] = useState('');
  const [isImageModalActive, setIsImageModalActive] = useState(false);
  const [isAgreementModalActive, setIsAgreementModalActive] = useState(false);
  const [webAgreement, setWebAgreement] = useState(false);
  const [policyValidation, setPolicyValidation] = useState({});

  const { control, handleSubmit, watch, formState: { errors }, setValue } = useForm({
    defaultValues: {
      policynbr: '',
      effdate: null,
      authcode: ''
    }
  });

  const watchedPolicyNumber = watch('policynbr');
  const watchedEffDate = watch('effdate');

  useEffect(() => {
    // Reset error and state when user changes input
    setIsRegistered(false);
    setIsAllowRegister(false);
    setIsValidPolicy(true);
    setError({ active: false, message: '', error: false, status: 200 });
    setValue('authcode', '');
    setWebAgreement(false);

    if (watchedPolicyNumber && watchedPolicyNumber.length > 6 && watchedEffDate) {
      checkRegisteredPolicy();
    }
  }, [watchedPolicyNumber, watchedEffDate, setValue]);

  const checkRegisteredPolicy = async () => {
    try {
      // Check if policy is valid
      const policyholderAuthCodeResult = await PolicyholderAuthCode({
        policyNumber: watchedPolicyNumber,
        effdate: moment(watchedEffDate).format('YYYY-MM-DD')
      });

      if (typeof policyholderAuthCodeResult.result !== 'undefined' && !policyholderAuthCodeResult.result) {
        setIsValidPolicy(false);
        setIsAllowRegister(false);
        setIsRegistered(false);
        setError({
          active: true,
          message: 'Policy Number or Effective Date is invalid. Please check and correct them.',
          error: 'Invalid Policy',
          status: 404
        });
        return;
      } else {
        setIsValidPolicy(true);
        setError({ active: false, message: '', error: false, status: 200 });
      }

      // Check if policy is already registered
      const policyholderAlreadyExistsResult = await PolicyholderAlreadyRegistered({
        policyNumber: watchedPolicyNumber,
        effdate: moment(watchedEffDate).format('YYYY-MM-DD')
      });

      if (typeof policyholderAlreadyExistsResult.result !== 'undefined' && 
          policyholderAlreadyExistsResult.result === false) {
        setIsAllowRegister(true);
        setIsRegistered(false);
      } else {
        setIsRegistered(true);
        setIsAllowRegister(false);
        setRegisteredName(policyholderAlreadyExistsResult.userFullName);
      }
    } catch (error) {
      console.error('Policy validation error:', error);
      setIsValidPolicy(false);
      setIsAllowRegister(false);
      setIsRegistered(false);
      setError({
        active: true,
        message: error.message || 'Registration is not working. Please try again later.',
        error: error.error || 'Validation Error',
        status: error.status || 500
      });
    }
  };

  const onSubmit = async (data) => {
    setFormLoading(true);
    setError({ active: false, message: '', error: false, status: 200 });

    try {
      // Validate access token
      const validationResult = await ValidateAccessToken({
        policynbr: (data.policynbr * 1).toString(),
        effdate: moment(data.effdate).format('YYYY-MM-DD'),
        authcode: data.authcode
      });

      setPolicyValidation(validationResult);

      // Check if policyholder already exists
      const policyholderAlreadyExistsResult = await PolicyholderAlreadyExists({
        authCode: data.authcode
      });

      if (typeof policyholderAlreadyExistsResult.result !== 'undefined' && 
          policyholderAlreadyExistsResult.result === false) {
        navigate('/register', {
          state: { policyValidation: validationResult }
        });
      } else {
        navigate('/polexists', {
          state: { 
            existentPolicyholderFullName: policyholderAlreadyExistsResult.userFullName 
          }
        });
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      
      if (error.status === 409) {
        errorMessage = 'Policy Number or Web Access Code is not valid. Please provide valid information.';
      } else if (error.status === 410) {
        errorMessage = 'Web Access Code provided is already expired. Please provide another one.';
      }

      setError({
        active: true,
        message: errorMessage,
        error: error.error || 'Validation Error',
        status: error.status || 500
      });
    } finally {
      setFormLoading(false);
    }
  };

  const onCancel = () => {
    window.location.href = '/account-management';
  };

  const formatPolicyNumber = (value) => {
    return value.replace(/[^\d]/g, '');
  };

  const isFormValid = watchedPolicyNumber && watchedEffDate && 
                     (isAllowRegister ? webAgreement : true) && 
                     isValidPolicy && isAllowRegister;

  const agreementContent = (
    <div>
      <h4><strong>Policyholder Agreement</strong></h4>
      <div style={{ marginTop: '1rem' }}>
        <p><strong>
          I CERTIFY THAT I AM A BONA FIDE PRINCIPAL, EMPLOYEE, OR REPRESENTATIVE OF AN ENTITY WHICH IS CURRENTLY A
          POLICYHOLDER WITH REPUBLIC INDEMNITY COMPANY OF AMERICA OR REPUBLIC INDEMNITY COMPANY OF CALIFORNIA
          ("REPUBLIC INDEMNITY").
        </strong></p>
        
        <p><strong>
          I AGREE to protect the confidentiality of the information retrieved from this World Wide Web site. I
          further agree to limit my use of this information to activities defined by the terms and conditions set
          forth in our insurance policy contract and its endorsements.
        </strong></p>
        
        <p><strong>
          I AGREE to accept responsibility for controlling access of my employees, agents, or other bona fide
          representatives via the security features provided herein. Upon termination of the employment of any
          user hereunder, I shall promptly delete that employee or representative as a user.
        </strong></p>
        
        <p><strong>
          REPUBLIC INDEMNITY provides information as a service to its Policyholders. The information and service
          provided via this facility is provided as is without any warranty, expressed or implied. Republic
          Indemnity is not responsible for any material linked to its site, whether or not linked by license.
        </strong></p>
      </div>
    </div>
  );

  return (
    <div className="container">
      <div className="grid">
        <div className="col-12">
          <p>
            Please provide the following information so that we may verify that you are a bona fide Republic Indemnity
            Policyholder.
          </p>
        </div>
      </div>

      <Card>
        {error.active && (
          <Message 
            severity="error" 
            text={error.message}
            style={{ marginBottom: '1rem' }}
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid">
            <div className="col-6" style={{ width: '60%' }}>
              <div className="field">
                <label htmlFor="policynbr" className="block">Policy Number:</label>
                <div className="p-inputgroup">
                  <Controller
                    name="policynbr"
                    control={control}
                    rules={{ 
                      required: 'Policy Number is required',
                      minLength: { value: 8, message: 'Policy Number must be at least 8 digits' }
                    }}
                    render={({ field }) => (
                      <InputText
                        id="policynbr"
                        {...field}
                        onChange={(e) => field.onChange(formatPolicyNumber(e.target.value))}
                        className={classNames({ 'p-invalid': errors.policynbr })}
                        disabled={formLoading}
                        autoComplete="off"
                      />
                    )}
                  />
                  <Button
                    type="button"
                    icon="pi pi-question-circle"
                    className="p-button-info"
                    onClick={() => setIsImageModalActive(true)}
                  />
                </div>
                {errors.policynbr && <small className="p-error">{errors.policynbr.message}</small>}
              </div>

              <div className="field">
                <label htmlFor="effdate" className="block">Policy Effective Date:</label>
                <Controller
                  name="effdate"
                  control={control}
                  rules={{ required: 'Policy Effective Date is required' }}
                  render={({ field }) => (
                    <Calendar
                      id="effdate"
                      {...field}
                      showIcon
                      placeholder="Click to select..."
                      className={classNames({ 'p-invalid': errors.effdate })}
                      disabled={formLoading}
                    />
                  )}
                />
                {errors.effdate && <small className="p-error">{errors.effdate.message}</small>}
              </div>

              {isAllowRegister && (
                <span>
                  <div className="field">
                    <label htmlFor="authcode" className="block">
                      To ensure this is your policy, please enter your Web Access Code:
                    </label>
                    <Controller
                      name="authcode"
                      control={control}
                      rules={{ 
                        required: 'Web Access Code is required',
                        minLength: { value: 8, message: 'Web Access Code must be at least 8 characters' },
                        maxLength: { value: 9, message: 'Web Access Code must be at most 9 characters' }
                      }}
                      render={({ field }) => (
                        <InputText
                          id="authcode"
                          {...field}
                          className={classNames({ 'p-invalid': errors.authcode })}
                          disabled={formLoading}
                        />
                      )}
                    />
                    {errors.authcode && <small className="p-error">{errors.authcode.message}</small>}
                  </div>

                  <div className="field-checkbox">
                    <Checkbox
                      inputId="webAgreement"
                      checked={webAgreement}
                      onChange={(e) => setWebAgreement(e.checked)}
                      disabled={formLoading}
                    />
                    <label htmlFor="webAgreement" className="ml-2">
                      I agree with Republic's Website{' '}
                      <a href='#' onClick={() => setIsAgreementModalActive(true)}>Agreement</a>
                      .
                    </label>
                    {isAllowRegister && !webAgreement && (
                      <small className="p-error block">Please check this box to proceed</small>
                    )}
                  </div>
                </span>
              )}
            </div>
          </div>

          {isRegistered && (
            <Message 
              severity="error" 
              text={
                <>
                  <strong>Policyholder already registered</strong><br/>
                  This Policy Number is already registered.<br/>
                  Contact your user manager {registeredName} to add a user.<br/>
                </>
              }
              style={{ margin: '1rem 0' }}
            />
          )}

          {!isValidPolicy && (
            <Message 
              severity="error" 
              text={
                <>
                  <strong>Invalid Policy!</strong><br/>
                  Policy Number or Effective Date is invalid.<br/>
                  Please check and correct them.
                </>
              }
              style={{ margin: '1rem 0' }}
            />
          )}

          <div className="field" style={{ marginTop: '2rem' }}>
            <Button
              type="submit"
              label="Continue"
              severity="warning"
              disabled={!isFormValid}
              loading={formLoading}
              style={{ marginRight: '0.5rem' }}
            />
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              onClick={onCancel}
              disabled={formLoading}
            />
          </div>
        </form>
      </Card>

      {/* Policy Number Sample Modal */}
      <Dialog
        visible={isImageModalActive}
        onHide={() => setIsImageModalActive(false)}
        style={{ width: '90vw', maxWidth: '800px' }}
      >
        <div>
          <img 
            src={PolicyNumberSample} 
            alt="Policy Number Sample" 
            style={{ marginBottom: '1rem' }}
          />
          <img 
            src={PolicyInfoPage} 
            alt="Policy Info Page" 
            style={{ width: '700' }}
          />
        </div>
      </Dialog>

      {/* Agreement Modal */}
      <Dialog
        header="Policyholder Agreement"
        visible={isAgreementModalActive}
        onHide={() => setIsAgreementModalActive(false)}
        style={{ width: '90vw', maxWidth: '600px' }}
      >
        {agreementContent}
      </Dialog>
    </div>
  );
};

export default ValidateCode;
